import * as React from 'react';
import {faCalendarAlt, faExclamationCircle} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {forwardRef, useImperativeHandle, useLayoutEffect, useRef} from 'react';

import {dateObjectToString} from '../../../tools/date.functions';
import '../../../../client/ui/vendor/tiny-date-picker.scss';

interface DatePickerProps {
    autoFocus?: boolean;
    className?: string;
    error?: string;
    label: string;
    min?: string | number;
    name: string;
    onChange?: (pickerState: {target: HTMLInputElement} | string) => void;
    ref?: any;
    value?: string;
}

const DatePicker: React.FC<DatePickerProps> = forwardRef(
    ({autoFocus = false, className, error, label, min, name, onChange = () => null, value}, ref) => {
        const datePickerRef = useRef<HTMLInputElement>();
        const tinyDatePickerRef = useRef<any>(null);

        useImperativeHandle(ref, () => datePickerRef.current, []);

        // Build the date format for input
        const dateHelper = (originalDate: string | Date) => {
            if (!originalDate) {
                return null;
            }
            const date = new Date(originalDate);
            return dateObjectToString(date);
        };

        useLayoutEffect(() => {
            if (datePickerRef.current) {
                const TinyDatePicker = require('tiny-date-picker');
                tinyDatePickerRef.current = TinyDatePicker(datePickerRef.current, {
                    appendTo: datePickerRef.current,
                    mode: `dp-below`,
                    min,
                    format: (date: string) => dateHelper(date),
                });
                tinyDatePickerRef.current.on(`statechange`, (_, picker) => {
                    if (picker.state.hilightedDate === picker.state.selectedDate) {
                        const originalDate: Date = picker.state.selectedDate;
                        datePickerRef.current.value = dateHelper(originalDate);
                        onChange({target: datePickerRef.current});
                    }
                });
                if (autoFocus) {
                    datePickerRef.current.focus();
                }
                return () => {
                    tinyDatePickerRef.current.close();
                    tinyDatePickerRef.current.destroy();
                };
            }
        }, [autoFocus, min, onChange]);

        /**
         * Template
         */
        return (
            <div className={className}>
                <div className="tw-relative tw-mb-6 tw-flex tw-flex-nowrap tw-items-stretch tw-w-full">
                    <input
                        autoComplete="off"
                        className={`tw-py-1.5 tw-px-3 tw-text-base tw-font-normal tw-leading-normal tw-bg-white tw-border tw-rounded-sm tw-z-[2] tw-h-[52px] tw-pt-6 tw-pl-3 tw-mb-0 tw-min-w-0 tw-w-[1%] tw-flex tw-flex-grow tw-relative ${
                            error ? ' tw-border-red-500' : ''
                        }`}
                        defaultValue={dateHelper(value)}
                        id={name}
                        name={name}
                        onInput={(e) => {
                            e.currentTarget.value = dateHelper(e.currentTarget.value);
                            onChange(e.currentTarget.value);
                        }}
                        ref={datePickerRef}
                    />
                    {error && (
                        <FontAwesomeIcon
                            className={`tw-text-red-500 ${error ? '!tw-right-8 tw-mr-4' : ''}`}
                            icon={faExclamationCircle}
                            size="lg"
                        />
                    )}
                    <label
                        className="tw-mb-6 tw-z-10 tw-absolute tw-transition-[all_0.3s] tw-top-1 tw-left-[13px] tw-text-sm tw-text-gray-400 form-label after:!tw-content-[' *']"
                        htmlFor={name}
                    >
                        {label}
                    </label>
                    <div className="tw-ml-[-1px] tw-flex">
                        <button
                            className="tw-bg-white tw-flex tw-items-center tw-py-1.5 tw-px-3 tw-mb-0 tw-text-base tw-font-normal tw-leading-normal tw-text-center tw-whitespace-nowrap tw-border tw-rounded-sm tw-cursor-pointer"
                            onMouseDown={() => {
                                datePickerRef.current.focus();
                            }}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                        </button>
                    </div>
                </div>
                {error && <label className="tw-text-red-500">{error}</label>}
            </div>
        );
    },
);

export default DatePicker;
